import { useState } from 'react'
import { FlashIcon, ShieldIcon, GiftIcon } from '@repo/ui'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import HomeAltIcon from 'src/app/assets/icons/ico-home-alt.svg'
import BallPileIcon from 'src/app/assets/icons/ico-ball-pile.svg'
import BullseyeIcon from 'src/app/assets/icons/ico-bullseye.svg'
import RandomIcon from 'src/app/assets/icons/ico-random.svg'
import GlobeStandIcon from 'src/app/assets/icons/ico-globe-stand.svg'
import LocationCrosshairsIcon from 'src/app/assets/icons/ico-location-crosshairs.svg'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import FireIcon from 'src/app/assets/icons/ico-fire.svg'
import DroneIcon from 'src/app/assets/icons/ico-drone.svg'
import PersonSkiingIcon from 'src/app/assets/icons/ico-person-skiing.svg'
import CameraGoProIcon from 'src/app/assets/icons/ico-camera-gopro.svg'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import { useClientSideTranslation } from 'src/services/i18n'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { useGetCurrentLanguage, useGetGameBetConfig, useGetGameConfig } from 'src/store/game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { getSelectionTypeOptions, isBirdieGameType } from 'src/utils/gameTypeUtils'
import { useGetCurrentPlayer } from 'src/store/player'
import { BirdieGameRules } from 'src/components/BirdieBoard/BirdieGameRules'
import { MarbleItem } from '../../MarbleItem'
import { FullRtpTable } from './FullRtpTable'
import { SingleRtpTable } from './SingleRtpTable'
import { ErrorHandlingSection } from './rulesSections/ErrorHandlingSection'
import { PlacingABetSection } from './rulesSections/PlacingABetSection'
import { isExoticBetEnabled } from 'src/utils/isExoticBetEnabled'
import { hasLightning, resolveGameKey } from 'src/utils/effectiveRtp'

type Props = Readonly<{
  game: MarbleGame
}>

export default function GameRulesSidebar({ game }: Props) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage, 'gameRules')

  const player = useGetCurrentPlayer()
  const betConfig = useGetGameBetConfig(player)
  const gameConfig = useGetGameConfig()

  // Chip-placement reshapes several rules sections (multi-bet model,
  // auto-apply free bets/boosters, deferred-reveal lightning, no
  // streak system, no inventory UI). All chip-placement-specific copy
  // gates on this flag.
  const isChipPlacement =
    isKnownGameConfig(gameConfig) && gameConfig.bettingMode === 'chip-placement'

  // Not every game has Lightning Rounds configured. Coin Flip has none
  // (0 lightning rounds across 95,244 production rounds) and neither
  // does crash, so any Lightning copy or effective-RTP lift shown for
  // them advertises a boost that cannot fire.
  const gameHasLightning = hasLightning(resolveGameKey(game.id))

  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [expandedCard, setExpandedCard] = useState<string | null>(null)
  const [selectedWinMode, setSelectedWinMode] = useState<'winner' | 'any-order' | 'exact-order'>(
    'winner'
  )
  const resultsToSubmit = game.resultsToSubmit
  const selectionOptions = getSelectionTypeOptions(game.track.gameType, t)
  const isStairPongGame = game.track.gameType === 'stairpong'
  const isMarblesSurvivorGame = game.track.gameType === 'marbles_survivor'
  const isMarblesGame = game.track.gameType === 'marbles'
  const isDucksGame = game.track.gameType === 'ducks'
  const isCoinFlipGame = game.track.gameType === 'coin_flip'
  const isFishtankGame = game.track.gameType === 'fishtank'
  const isIrlGame = game.track.gameType === 'irl'
  const isDuckRiverGame = game.track.gameType === 'duck_river'
  const isSnowRunGame = game.track.gameType === 'snow_run'
  const isFastLaneGame = game.track.gameType === 'fast_lane'
  const isCrashGame = game.track.gameType === 'crash'

  // Map Fast Lane car names to their dominant accent color
  const fastLaneCarColors: Record<string, string> = {
    'Honda Civic': '#f59e0b',
    'Shelby Cobra': '#8fa3a8',
    'HW Wrap Speeder': '#9b59b6',
    'The Nash': '#4ade80',
    'Corvette Gasser': '#06b6d4',
    'Mad Splash': '#f59e0b',
    'Time Attaxi': '#d4c520',
    'The Haulinator': '#ef4444',
    'Honda CR-X': '#f97316',
    'Audi R8 Spyder': '#eab308'
  }

  const isIrlAltGame =
    game.track.gameType === 'irl_alt' ||
    game.track.gameType === 'duck_river' ||
    game.track.gameType === 'snow_run' ||
    game.track.gameType === 'social_betting'
  // Social Betting plays creator-uploaded clips rather than live CCTV —
  // the rules copy is reframed for that throughout this component.
  const isSocialBettingGame = game.track.gameType === 'social_betting'

  const handleSectionClick = (sectionId: string) => {
    setActiveSectionId(activeSectionId === sectionId ? null : sectionId)
  }

  const modeFixedOddsRandom = game.track.mode.type === 'fixed_odds_random'
  const modeFixedOddsFixed = game.track.mode.type === 'fixed_odds_fixed'
  const modeEqualOdds = game.track.mode.type === 'equal_odds'

  const modeEqualOddsShowStartLine = game.track.mode.type === 'equal_odds_show_start_line'

  const equalOdds = betConfig?.equalOdds ?? false

  // Show disclaimer for non-English languages
  const showTranslationDisclaimer = currentLanguage !== 'en' && currentLanguage !== 'en-soc'

  // Birdie is a rendering inside this panel, not a set of flags in it. Its
  // three putts fit none of the chip-placement blocks below (the marble list,
  // finish-line settlement, winner mode), so the whole body is its own
  // component, built from the panel's own blocks; the header and back button
  // in <RulesPanel> stay shared. One gate, one early return — the tree below
  // is untouched for every other game.
  if (isBirdieGameType(game.track.gameType)) {
    return <BirdieGameRules game={game} />
  }

  return (
    <div className="panel-game-rules">
      {showTranslationDisclaimer && (
        <div className="info-box margin-bottom ai-translation-disclaimer">
          <div className="ai-translation-disclaimer-content">
            <div className="ai-translation-disclaimer-text">
              ℹ️{' '}
              {t(
                'This translation was generated using AI and may contain errors. English is the reference language for all game rules and terms.'
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={`section section-collapsible ${activeSectionId === 'basic-rules' ? 'open' : ''}`}
      >
        <div className="section-header" onClick={() => handleSectionClick('basic-rules')}>
          <h3>{t('Basic Rules')}</h3>
          <div className="section-header-icon">
            <AngleRightIcon />
          </div>
        </div>
        <div className="section-content">
          {(isIrlGame || isIrlAltGame) && (
            <div className="your-goal-hero">
              <div className="your-goal-hero__icon">
                <BullseyeIcon />
              </div>
              <h3 className="your-goal-hero__title">{t('Your Mission')}</h3>
              <p className="your-goal-hero__text">
                {isSnowRunGame
                  ? t(
                      'Watch skiers and snowboarders through GoPro POV, aerial drones, and slope-side cameras. Predict how many hit the tracking zones — over, under, range or exact. The mountain never stops.'
                    )
                  : isDuckRiverGame
                    ? t(
                        'Real segments from the duck river. You predict how many ducks the 🤖 AI will detect floating past the zone. Bet under, over, range or exact value.'
                      )
                    : isIrlAltGame
                      ? isSocialBettingGame
                        ? t(
                            'Real clips uploaded by our community of creators. You predict how many things the 🤖 AI will count in the clip. Bet under, over, range or exact value.'
                          )
                        : t(
                            'Real CCTV feeds from around the world. You predict how many objects the 🤖 AI will detect crossing the zone. Bet under, over, range or exact value.'
                          )
                      : t(
                          'Watch a live traffic camera and predict the car count! Bet on Over, Under, or the exact number of cars passing through during the round.'
                        )}
              </p>
              <div className="your-goal-hero__disclaimer">
                {t(
                  "The 🤖 AI count is final — like a referee's call. The on-screen count is what all bets settle against."
                )}
              </div>
            </div>
          )}
          {isFastLaneGame && (
            <>
              <div className="your-goal-hero">
                <div className="your-goal-hero__icon">
                  <FlagCheckeredIcon />
                </div>
                <h3 className="your-goal-hero__title">{t('Your Goal')}</h3>
                <p className="your-goal-hero__text">
                  {t(
                    '20 cars on the treadmill (10 unique, 2 of each) — pick up to 3 and predict which one is the last car standing!'
                  )}
                </p>
              </div>
              <div className="game-flow-timeline fast-lane-how-it-works">
                <div className="flow-step flow-step--yellow">
                  <div className="flow-step__number">1</div>
                  <div className="flow-step__content">
                    <h4>{t('Pick Your Cars')}</h4>
                    <p>{t('Select up to 3 cars before betting closes.')}</p>
                  </div>
                </div>
                <div className="flow-step flow-step--red">
                  <div className="flow-step__number">2</div>
                  <div className="flow-step__content">
                    <h4>{t('Treadmill Starts')}</h4>
                    <p>{t('Cars are eliminated one by one as they fall off the treadmill.')}</p>
                  </div>
                </div>
                <div className="flow-step flow-step--green">
                  <div className="flow-step__number">3</div>
                  <div className="flow-step__content">
                    <h4>{t('Last Car Wins')}</h4>
                    <p>{t('If your car is the last one standing, you win!')}</p>
                  </div>
                </div>
              </div>
            </>
          )}
          {isCrashGame && (
            <>
              <div className="your-goal-hero">
                <div className="your-goal-hero__icon">
                  <BullseyeIcon />
                </div>
                <h3 className="your-goal-hero__title">{t('Your Goal')}</h3>
                <p className="your-goal-hero__text">
                  {t(
                    'A real street, a camera, and a multiplier climbing from 1.00x. Cash out before someone walks into frame — the instant they do, the round crashes. Hold longer for a bigger win, but the walk-in can come at any second. Cash out in time and you keep your stake times the multiplier.'
                  )}
                </p>
                <div className="your-goal-hero__disclaimer">
                  {t(
                    'The crash point is fixed before the round starts and provably fair — the footage just reveals it.'
                  )}
                </div>
              </div>
              <div className="game-flow-timeline">
                <div className="flow-step flow-step--yellow">
                  <div className="flow-step__number">1</div>
                  <div className="flow-step__content">
                    <h4>{t('Place Your Stake')}</h4>
                    <p>
                      {t(
                        'Set your amount and place up to two stakes before the round starts. Add an auto cash-out target if you want the game to lock in a multiplier for you.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flow-step flow-step--red">
                  <div className="flow-step__number">2</div>
                  <div className="flow-step__content">
                    <h4>{t('Watch It Climb')}</h4>
                    <p>
                      {t(
                        'When the round starts, the multiplier rises from 1.00x — and your potential win rises with it. Every moment you hold is worth more.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flow-step flow-step--green">
                  <div className="flow-step__number">3</div>
                  <div className="flow-step__content">
                    <h4>{t('Cash Out In Time')}</h4>
                    <p>
                      {t(
                        'Cash out before the crash to bank your stake times the current multiplier. Set an auto cash-out and it fires the instant your target is hit.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="locations-info">
                <GlobeStandIcon />
                <span>
                  {t(
                    'Every round plays real street footage — and the person you’re watching for really does walk into frame.'
                  )}
                </span>
              </div>
            </>
          )}
          {!isIrlGame && !isIrlAltGame && !isFastLaneGame && !isCrashGame && (
            <div className="info-card">
              <div className="info-card-icon">
                <BullseyeIcon />
              </div>
              <div className="info-card-content">
                <h3 className="info-card-title">{t('Your Goal')}</h3>
                <div className="info-card-description">
                  {isCoinFlipGame
                    ? t('Predict heads or tails!')
                    : isFishtankGame
                      ? t(
                          'Each round, a coin is dropped into the tank. Predict where it will land: on the left or right side of the tank, inside the treasure chest, on in the white or red cup.'
                        )
                      : isMarblesSurvivorGame
                        ? t('{{totalMarbles}} {{plural}} race - predict who finishes last!', {
                            totalMarbles: game.totalMarbles,
                            plural: selectionOptions.plural
                          })
                        : t('{{totalMarbles}} {{plural}} race - predict who finishes 1st!', {
                            totalMarbles: game.totalMarbles,
                            plural: selectionOptions.plural
                          })}
                </div>
              </div>
            </div>
          )}
          {isIrlAltGame && (
            <>
              <div className="betting-options-section">
                <h3 className="betting-options-section__title">{t('Betting Options')}</h3>
                <p className="betting-options-section__subtitle">
                  {t('Betting options and target numbers are shown in the overlay.')}
                </p>
                <div className="betting-options-grid">
                  <div className="bet-option bet-option--under">
                    <div className="bet-option__img">
                      <img src="/assets/game-ui-cctv/under.webp" alt="Under" />
                    </div>
                    <span className="bet-option__name">{t('Under')}</span>
                    <span className="bet-option__desc">{t('Fewer than the target number')}</span>
                  </div>
                  <div className="bet-option bet-option--over">
                    <div className="bet-option__img">
                      <img src="/assets/game-ui-cctv/over.webp" alt="Over" />
                    </div>
                    <span className="bet-option__name">{t('Over')}</span>
                    <span className="bet-option__desc">{t('More than the target number')}</span>
                  </div>
                  <div className="bet-option bet-option--range">
                    <div className="bet-option__img">
                      <img src="/assets/game-ui-cctv/range.webp" alt="Range" />
                    </div>
                    <span className="bet-option__name">{t('Range')}</span>
                    <span className="bet-option__desc">{t('Within a specific range')}</span>
                  </div>
                  <div className="bet-option bet-option--exact">
                    <div className="bet-option__img">
                      <img src="/assets/game-ui-cctv/exact.webp" alt="Exact" />
                    </div>
                    <span className="bet-option__name">{t('Exact')}</span>
                    <span className="bet-option__desc">
                      {t('2 numbers shown - win if count matches either')}
                    </span>
                  </div>
                </div>
              </div>
              {isSnowRunGame && (
                <div className="camera-modes-section">
                  <h3 className="betting-options-section__title">{t('Camera Modes')}</h3>
                  <p className="betting-options-section__subtitle">
                    {t('Three camera styles, three ways to count.')}
                  </p>
                  <div className="how-it-works-grid">
                    <div
                      className={`how-it-works-card how-it-works-card--cyan ${expandedCard === 'cam-static' ? 'expanded' : ''}`}
                      onClick={() =>
                        setExpandedCard(expandedCard === 'cam-static' ? null : 'cam-static')
                      }
                    >
                      <div className="how-it-works-card__icon">
                        <PersonSkiingIcon />
                      </div>
                      <div className="how-it-works-card__body">
                        <div className="how-it-works-card__header">
                          <h4>{t('Slope-Side Camera')}</h4>
                          <span className="how-it-works-card__tag">{t('STATIC')}</span>
                        </div>
                        <p>
                          {t('Fixed camera with a crossing line. Skier crosses — counted once.')}
                        </p>
                        <div className="how-it-works-card__detail">
                          <p>
                            {t(
                              'A single detection line is placed across the slope. When a skier crosses it, they are counted once — simple and fun.'
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="how-it-works-card__chevron">
                        <AngleRightIcon />
                      </div>
                    </div>

                    <div
                      className={`how-it-works-card how-it-works-card--orange ${expandedCard === 'cam-pov' ? 'expanded' : ''}`}
                      onClick={() => setExpandedCard(expandedCard === 'cam-pov' ? null : 'cam-pov')}
                    >
                      <div className="how-it-works-card__icon">
                        <CameraGoProIcon />
                      </div>
                      <div className="how-it-works-card__body">
                        <div className="how-it-works-card__header">
                          <h4>{t('GoPro POV')}</h4>
                          <span className="how-it-works-card__tag">{t('DWELL')}</span>
                        </div>
                        <p>
                          {t(
                            'First-person view down the slope. A countdown arc fills on each person — when it completes, they are counted and the cycle repeats every 10 seconds.'
                          )}
                        </p>
                        <div className="how-it-works-card__detail">
                          <p>
                            {t(
                              "The GoPro stays close to the same skiers, so a dwell-time system re-counts them while they remain in the zone. Leave the zone and the countdown pauses. A streak badge (x1, x2, x3) tracks each person's count."
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="how-it-works-card__chevron">
                        <AngleRightIcon />
                      </div>
                    </div>

                    <div
                      className={`how-it-works-card how-it-works-card--purple ${expandedCard === 'cam-drone' ? 'expanded' : ''}`}
                      onClick={() =>
                        setExpandedCard(expandedCard === 'cam-drone' ? null : 'cam-drone')
                      }
                    >
                      <div className="how-it-works-card__icon">
                        <DroneIcon />
                      </div>
                      <div className="how-it-works-card__body">
                        <div className="how-it-works-card__header">
                          <h4>{t('Aerial Drone')}</h4>
                          <span className="how-it-works-card__tag">{t('SCAN')}</span>
                        </div>
                        <p>
                          {t(
                            "Bird's-eye view with a tracking zone that drifts across the slope. The AI counts what it sees — it may miss a skier or re-count one. That's part of the game."
                          )}
                        </p>
                        <div className="how-it-works-card__detail">
                          <p>
                            {t(
                              'Altitude, overlapping paths, and snow conditions can affect detection. The drifting zone keeps every round dynamic. What the drone sees is what counts — all players get the same result.'
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="how-it-works-card__chevron">
                        <AngleRightIcon />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="locations-info">
                <GlobeStandIcon />
                <span>
                  {isSnowRunGame
                    ? t(
                        'Real footage from real mountains — yes, it really looks this good. Every round is a unique segment from one of our ski slope locations.'
                      )
                    : isDuckRiverGame
                      ? t(
                          'The camera captures footage from the duck river. Each round is a unique segment.'
                        )
                      : isSocialBettingGame
                        ? t(
                            'Every round is a clip uploaded by a community creator — streets, events, anywhere they point a camera.'
                          )
                        : t(
                            'Locations are selected automatically based on time of day and traffic patterns. The current location is shown during betting.'
                          )}
                </span>
              </div>
            </>
          )}
          {!isCoinFlipGame &&
            !isFishtankGame &&
            !isIrlGame &&
            !isIrlAltGame &&
            !isFastLaneGame &&
            !isCrashGame && (
              <div className="info-card info-card-with-betting-modes">
                <div className="info-card-icon-top">
                  <div className="info-card-icon">
                    <FlagCheckeredIcon />
                  </div>
                  <div className="info-card-content">
                    <h3 className="info-card-title">{t('Settlement Rules')}</h3>
                    <div className="info-card-description">
                      <ul>
                        <li>
                          {isStairPongGame
                            ? t('First {{singular}} to enter the funnel wins', {
                                singular: selectionOptions.singular
                              })
                            : isDucksGame
                              ? t('First {{singular}} body part to cross the finish line wins', {
                                  singular: selectionOptions.singular
                                })
                              : isMarblesSurvivorGame
                                ? t('Last {{singular}} on the track wins', {
                                    singular: selectionOptions.singular
                                  })
                                : isFastLaneGame
                                  ? t('Last {{singular}} on the treadmill wins', {
                                      singular: selectionOptions.singular
                                    })
                                  : t('First {{singular}} to cross finish line wins', {
                                      singular: selectionOptions.singular
                                    })}
                        </li>
                        {(isDucksGame || isMarblesGame) && (
                          <li>
                            {t(
                              'Any {{plural}} that leaves the finish line or gets stuck on the track is disqualified and will be placed at the end of the finishing order.',
                              {
                                plural: selectionOptions.plural
                              }
                            )}
                          </li>
                        )}
                        <li>
                          {isStairPongGame
                            ? t(
                                'If no {{singular}} enters the funnel, the race will be repeated for a maximum of 3 times. If the race is repeated 3 times and still does not have a valid result, the race will be cancelled and all bets will be refunded.',
                                { singular: selectionOptions.singular }
                              )
                            : t(
                                'Need {{min}}+ finishers or race gets cancelled and all bets refunded.',
                                { min: resultsToSubmit }
                              )}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Betting Modes (Winner / Any Order / Exact Order) only
                  apply to classic single-bet mode. Chip-placement is
                  always multi-pick — each chip is its own
                  Pick-Winner bet — so the mode selector doesn't apply.
                  Hidden entirely for chip-placement; the betting UI
                  itself communicates the multi-pick model. */}
                {!isChipPlacement && (
                  <div className="betting-modes-section">
                    <div className="betting-modes-header">
                      <div className="betting-modes-icon">
                        <BallPileIcon />
                      </div>
                      <h4 className="betting-modes-title">{t('Betting Modes')}</h4>
                    </div>

                    <div className="bet-mode-selector">
                      <button
                        className={`mode-btn ${selectedWinMode === 'winner' ? 'active' : ''}`}
                        onClick={() => setSelectedWinMode('winner')}
                      >
                        <div className="mode-btn-icon">
                          <FlagCheckeredIcon />
                        </div>
                        <div className="mode-btn-text">{t('Winner')}</div>
                        <div className="difficulty-easy">{t('EASY')}</div>
                      </button>
                      {!isFastLaneGame && isExoticBetEnabled(game.maxPickableMarbles) && (
                        <>
                          <button
                            className={`mode-btn ${selectedWinMode === 'any-order' ? 'active' : ''}`}
                            onClick={() => setSelectedWinMode('any-order')}
                          >
                            <div className="mode-btn-icon">
                              <RandomIcon />
                            </div>
                            <div className="mode-btn-text">{t('Any Order')}</div>
                            <div className="difficulty-medium">{t('MEDIUM')}</div>
                          </button>
                          <button
                            className={`mode-btn ${selectedWinMode === 'exact-order' ? 'active' : ''}`}
                            onClick={() => setSelectedWinMode('exact-order')}
                          >
                            <div className="mode-btn-icon">
                              <BullseyeIcon />
                            </div>
                            <div className="mode-btn-text">{t('Exact Order')}</div>
                            <div className="difficulty-hard">{t('HARD')}</div>
                          </button>
                        </>
                      )}
                    </div>

                    <div className="win-mode-content">
                      {isStairPongGame && selectedWinMode === 'winner' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick up to {{max}} {{singular}}', {
                              max: game.maxPickableMarbles,
                              singular: selectionOptions.singular
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t('If the {{singular}} you pick enters the funnel first, you win!', {
                              singular: selectionOptions.singular
                            })}
                          </div>
                        </div>
                      )}
                      {isMarblesSurvivorGame && selectedWinMode === 'winner' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick up to {{max}} {{singular}}', {
                              max: game.maxPickableMarbles,
                              singular: selectionOptions.singular
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t('If the {{singular}} you pick finishes last, you win!', {
                              singular: selectionOptions.singular
                            })}
                          </div>
                        </div>
                      )}
                      {isFastLaneGame && selectedWinMode === 'winner' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick up to {{max}} {{plural}}', {
                              max: game.maxPickableMarbles,
                              plural: selectionOptions.plural
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t(
                              'If the {{singular}} you pick is the last one on the treadmill, you win!',
                              {
                                singular: selectionOptions.singular
                              }
                            )}
                          </div>
                        </div>
                      )}
                      {(isDucksGame || isMarblesGame) && selectedWinMode === 'winner' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick up to {{max}} {{plural}}', {
                              max: game.maxPickableMarbles,
                              plural: selectionOptions.plural
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t(
                              'If any of the {{plural}} you pick crosses the finish line first, you win!',
                              {
                                plural: selectionOptions.plural
                              }
                            )}
                          </div>
                        </div>
                      )}
                      {!isStairPongGame && selectedWinMode === 'any-order' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick 2-3 {{plural}} in any order', {
                              plural: selectionOptions.plural
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t('ALL must finish in top positions in any order')}
                          </div>
                          <div className="example">
                            💡 {t('Example: Pick Red + Blue → Both must finish 1st or 2nd')}
                          </div>
                        </div>
                      )}
                      {selectedWinMode === 'exact-order' && (
                        <div className="win-mode-content-container">
                          <div className="win-mode-content-title">
                            {t('Pick 2-3 {{plural}} in exact order', {
                              plural: selectionOptions.plural
                            })}
                          </div>
                          <div className="win-mode-content-description">
                            {t('ALL must finish in EXACT order you predict')}
                          </div>
                          <div className="example">
                            💡 {t('Example: Red 1st, Blue 2nd → Must finish exactly as predicted')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

          {isFastLaneGame && (
            <div className="fast-lane-cars-grid">
              <div className="list-marbles-header">
                <div className="list-marbles-icon">
                  <FlagCheckeredIcon />
                </div>
                <h4 className="list-marbles-title">
                  {t('{{plural}} on the track', { plural: selectionOptions.plural })}
                </h4>
              </div>
              <div className="fast-lane-cars-list">
                {game.marbles.map((marble: Marble) => {
                  const accent = fastLaneCarColors[marble.color] ?? '#94a3b8'
                  return (
                    <div
                      className="fast-lane-car-item"
                      key={marble.uuid}
                      style={{ '--car-accent': accent } as React.CSSProperties}
                    >
                      <div className="fast-lane-car-item__image">
                        <MarbleItem className={`marble-${marble.color}`} marble={marble} />
                      </div>
                      <span className="fast-lane-car-item__name">
                        {getTranslatedMarbleColor(marble.color, t)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
          {!isCoinFlipGame &&
            !isFishtankGame &&
            !isIrlGame &&
            !isIrlAltGame &&
            !isFastLaneGame &&
            !isCrashGame && (
              <div className="info-card info-card-with-marbles">
                <div className="info-card-icon-top">
                  <div className="info-card-icon">
                    {isStairPongGame ? <BallPileIcon /> : <HomeAltIcon />}
                  </div>
                  <div className="info-card-content">
                    <h3 className="info-card-title">{t('Start Setup')}</h3>
                    <div className="info-card-description">
                      {isStairPongGame ? (
                        t(
                          '{{marbleCount}} {{plural}} randomly shuffled in bucket, shown when race starts',
                          { marbleCount: game.totalMarbles, plural: selectionOptions.plural }
                        )
                      ) : (
                        <>
                          {(modeEqualOdds || modeEqualOddsShowStartLine) &&
                            t('Random positions for fair play')}
                          {(modeFixedOddsRandom || modeFixedOddsFixed) &&
                            t('Fixed odds with set positions')}
                          {' - '}
                          {(modeEqualOdds || modeFixedOddsRandom) &&
                            t('revealed after betting closes')}
                          {(modeEqualOddsShowStartLine || modeFixedOddsFixed) &&
                            t('visible when betting opens')}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="list-marbles-header">
                  <div className="list-marbles-icon">
                    <FlagCheckeredIcon />
                  </div>
                  <h4 className="list-marbles-title">
                    {t('{{plural}} on the track', { plural: selectionOptions.plural })}
                  </h4>
                </div>

                <div className="list-marbles">
                  {game.marbles.map((marble: Marble) => (
                    <div className="intro-marble" key={marble.uuid}>
                      <MarbleItem
                        className={`marble-${marble.color}`}
                        marble={marble}
                        tooltip={getTranslatedMarbleColor(marble.color, t)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {(isIrlGame || isIrlAltGame) && (
        <div
          className={`section section-collapsible ${
            activeSectionId === 'how-it-works' ? 'open' : ''
          }`}
        >
          <div className="section-header" onClick={() => handleSectionClick('how-it-works')}>
            <h3>{t('How It Works')}</h3>
            <div className="section-header-icon">
              <AngleRightIcon />
            </div>
          </div>
          <div className="section-content">
            <div className="how-it-works-grid">
              <div
                className={`how-it-works-card how-it-works-card--cyan ${expandedCard === 'footage' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'footage' ? null : 'footage')}
              >
                <div className="how-it-works-card__icon">
                  <GlobeStandIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>
                      {isSnowRunGame
                        ? t('Real Footage, Real Mountains')
                        : isSocialBettingGame
                          ? t('Real Footage, Real Creators')
                          : t('Real Footage, Near Real-Time')}
                    </h4>
                    <span className="how-it-works-card__tag">
                      {isSnowRunGame ? '100% REAL' : isSocialBettingGame ? 'COMMUNITY' : 'LIVE'}
                    </span>
                  </div>
                  <p>
                    {isSnowRunGame
                      ? t(
                          'Real footage from ski slopes — GoPro POV, aerial drones, and slope-side cams. Every round is a unique segment.'
                        )
                      : isDuckRiverGame
                        ? t('Live footage from the duck river. Every round is a unique segment.')
                        : isSocialBettingGame
                          ? t(
                              "Clips uploaded by our community of creators. Every round plays a different creator's clip."
                            )
                          : t(
                              'Real-world CCTV from cities worldwide. Every round is a fresh, unique segment — never reused.'
                            )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {isSnowRunGame
                        ? t(
                            'Each round uses a random camera angle with its own counting rules. The footage is 100% real — it just looks that good.'
                          )
                        : isDuckRiverGame
                          ? t(
                              'The stream runs from the duck river. Each round captures a segment of the action.'
                            )
                          : isSocialBettingGame
                            ? t(
                                "Creators upload their own footage from anywhere. Each round plays one creator's clip, start to finish."
                              )
                            : t(
                                'Streams run 24/7 from cities around the world. Each round captures a new segment within the last ~60 seconds.'
                              )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>

              <div
                className={`how-it-works-card how-it-works-card--orange ${expandedCard === 'detection' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'detection' ? null : 'detection')}
              >
                <div className="how-it-works-card__icon">
                  <LocationCrosshairsIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>
                      {'🤖 '}
                      {t('AI Detection')}
                    </h4>
                    <span className="how-it-works-card__tag">🤖 AI</span>
                  </div>
                  <p>
                    {t(
                      'Our AI scans every frame. Orange brackets = tracked. Green flash = counted.'
                    )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {isSnowRunGame
                        ? t(
                            'Static cams flash green on crossing. POV shows a countdown arc and streak badge. Drone marks skiers as they are detected. Every count is visible on screen.'
                          )
                        : isDuckRiverGame
                          ? t(
                              'A progress bar fills as ducks float through the detection zone, showing exactly what the AI sees in real time.'
                            )
                          : t(
                              'A progress bar fills as objects move through the detection zone, showing exactly what the AI sees in real time.'
                            )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>

              <div
                className={`how-it-works-card how-it-works-card--green ${expandedCard === 'referee' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'referee' ? null : 'referee')}
              >
                <div className="how-it-works-card__icon">
                  <ShieldIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>
                      {'🤖 '}
                      {t('The AI is the Referee')}
                    </h4>
                    <span className="how-it-works-card__tag">🤖 FINAL</span>
                  </div>
                  <p>{t("The AI count is final — like a referee's call in sport.")}</p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {isSnowRunGame
                        ? t(
                            "You are predicting the AI's final count — not a head count. What the AI sees is what counts."
                          )
                        : isDuckRiverGame
                          ? t(
                              "The game isn't 'how many floated past' — it's 'how many the AI detected.'"
                            )
                          : isSocialBettingGame
                            ? t(
                                "The game isn't 'how many were really there' — it's 'how many the AI detected.'"
                              )
                            : t(
                                "The game isn't 'how many drove past' — it's 'how many the AI detected.'"
                              )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>

              <div
                className={`how-it-works-card how-it-works-card--purple ${expandedCard === 'fairness' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'fairness' ? null : 'fairness')}
              >
                <div className="how-it-works-card__icon">
                  <BullseyeIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>{t('Provably Fair')}</h4>
                    <span className="how-it-works-card__tag">FAIR</span>
                  </div>
                  <p>
                    {t(
                      'Odds are locked after the AI counts, before you see the result. Same RTP every round.'
                    )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {isSnowRunGame
                        ? t(
                            'If the AI counts 8, all bets settle on 8. Same footage, same rules, same result for every player.'
                          )
                        : isDuckRiverGame
                          ? t(
                              "If the AI counts 8 ducks, all bets settle around 8 — not what a human might count. A missed duck doesn't advantage or disadvantage any bet."
                            )
                          : t(
                              "If the AI counts 8, all bets settle around 8 — not what a human might count. A missed detection doesn't advantage or disadvantage any bet."
                            )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCrashGame && (
        <div
          className={`section section-collapsible ${
            activeSectionId === 'how-it-works' ? 'open' : ''
          }`}
        >
          <div className="section-header" onClick={() => handleSectionClick('how-it-works')}>
            <h3>{t('How It Works')}</h3>
            <div className="section-header-icon">
              <AngleRightIcon />
            </div>
          </div>
          <div className="section-content">
            <div className="how-it-works-grid">
              <div
                className={`how-it-works-card how-it-works-card--cyan ${expandedCard === 'footage' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'footage' ? null : 'footage')}
              >
                <div className="how-it-works-card__icon">
                  <GlobeStandIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>{t('Real Footage, Real Streets')}</h4>
                    <span className="how-it-works-card__tag">REAL</span>
                  </div>
                  <p>
                    {t(
                      'Every round plays real street footage. It’s genuine — the walk-in you’re waiting on is a real person stepping into frame.'
                    )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {t(
                        'Nothing is staged for your stake — the footage is matched to a crash point that was already decided, so the walk-in lands exactly on cue.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>

              <div
                className={`how-it-works-card how-it-works-card--orange ${expandedCard === 'walkin' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'walkin' ? null : 'walkin')}
              >
                <div className="how-it-works-card__icon">
                  <LocationCrosshairsIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>{t('The Walk-In Is the Crash')}</h4>
                    <span className="how-it-works-card__tag">CRASH</span>
                  </div>
                  <p>
                    {t(
                      'The multiplier climbs while the street is clear. The instant someone enters the frame, the round crashes and the multiplier locks.'
                    )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {t(
                        'There’s no way to know when the walk-in lands — that’s the thrill. The only safe move is to cash out while you’re ahead.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>

              <div
                className={`how-it-works-card how-it-works-card--purple ${expandedCard === 'fairness' ? 'expanded' : ''}`}
                onClick={() => setExpandedCard(expandedCard === 'fairness' ? null : 'fairness')}
              >
                <div className="how-it-works-card__icon">
                  <ShieldIcon />
                </div>
                <div className="how-it-works-card__body">
                  <div className="how-it-works-card__header">
                    <h4>{t('Provably Fair')}</h4>
                    <span className="how-it-works-card__tag">FAIR</span>
                  </div>
                  <p>
                    {t(
                      'The crash point is decided before the round starts, from a secret seed committed in advance. Same 94% RTP every round.'
                    )}
                  </p>
                  <div className="how-it-works-card__detail">
                    <p>
                      {t(
                        'After the round the seed is revealed, so anyone can recompute the exact crash point and confirm it was fixed before betting closed. Open any past round to check it yourself.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="how-it-works-card__chevron">
                  <AngleRightIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <PlacingABetSection
        flags={{ isBirdie: false, isChipPlacement, isCrashGame }}
        game={game}
        onToggle={() => handleSectionClick('placing-a-bet')}
        open={activeSectionId === 'placing-a-bet'}
      />
      <div
        className={`section section-collapsible ${activeSectionId === 'game-flow' ? 'open' : ''}`}
      >
        <div className="section-header" onClick={() => handleSectionClick('game-flow')}>
          <h3>{t('Game Flow')}</h3>
          <div className="section-header-icon">
            <AngleRightIcon />
          </div>
        </div>
        <div className="section-content">
          <div className="game-flow-timeline">
            <div className="flow-step flow-step--yellow">
              <div className="flow-step__number">1</div>
              <div className="flow-step__content">
                <h4>{t('Betting Phase')}</h4>
                <p>
                  {isCrashGame
                    ? t('The next street is revealed. Place your stakes before the timer runs out.')
                    : isIrlGame || isIrlAltGame
                      ? t('Location revealed. Place your bets before time runs out.')
                      : t('Place your bets before time runs out.')}
                </p>
              </div>
            </div>

            <div className="flow-step flow-step--red">
              <div className="flow-step__number">2</div>
              <div className="flow-step__content">
                <h4>
                  {isCrashGame
                    ? t('Climb Phase')
                    : isIrlGame || isIrlAltGame
                      ? t('Counting Phase')
                      : t('Race Starting')}
                </h4>
                <p>
                  {isCrashGame
                    ? t('The round starts and the multiplier climbs from 1.00x. Cash out any time.')
                    : isSnowRunGame
                      ? t('AI counts skiers entering the tracking zones.')
                      : isDuckRiverGame
                        ? t('AI counts ducks floating past the detection zone.')
                        : isIrlGame || isIrlAltGame
                          ? t('AI counts objects crossing the detection zone.')
                          : t('Race begins. No more bets accepted.')}
                </p>
              </div>
            </div>

            <div className="flow-step flow-step--blue">
              <div className="flow-step__number">3</div>
              <div className="flow-step__content">
                <h4>
                  {isCrashGame
                    ? t('Crash')
                    : isIrlGame || isIrlAltGame
                      ? t('Count Locked')
                      : t('Race Ended')}
                </h4>
                <p>
                  {isCrashGame
                    ? t('Someone walks into frame — the multiplier locks and the round crashes.')
                    : isIrlGame || isIrlAltGame
                      ? t('Final count confirmed.')
                      : t('Results recording and winner verification.')}
                </p>
              </div>
            </div>

            <div className="flow-step flow-step--green">
              <div className="flow-step__number">4</div>
              <div className="flow-step__content">
                <h4>
                  {isCrashGame || isIrlGame || isIrlAltGame
                    ? t('Round Settled')
                    : t('Race Settled')}
                </h4>
                <p>
                  {isCrashGame
                    ? t('Cashed-out stakes are paid at your multiplier. The next round begins.')
                    : isIrlGame || isIrlAltGame
                      ? t('Winnings paid out. Next round begins.')
                      : t('Winnings paid out. Next race begins.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Promotional / bonus features (Lightning Rounds, Streak Multipliers,
          Daily Check-in, Player Levels, boosters, free bets) do not apply to
          crash — hide the whole Game Features section for crash. */}
      {!isCrashGame && (
        <div
          className={`section section-collapsible ${
            activeSectionId === 'game-features' ? 'open' : ''
          }`}
        >
          <div className="section-header" onClick={() => handleSectionClick('game-features')}>
            <h3>{t('Game Features')}</h3>
            <div className="section-header-icon">
              <AngleRightIcon />
            </div>
          </div>
          <div className="section-content">
            <div className="feature-cards-grid">
              {/* Lightning Rounds don't fire on crash — register_bet.go
                gates the lightning factor behind !round.IsCrashGame and
                the crash PotentialPayout override discards any boost.
                Coin Flip has no lightning tiers configured at all. Hide
                the card for any game without them rather than advertise
                a boost that cannot fire. */}
              {!isCrashGame && gameHasLightning && (
                <div className="feature-card feature-card--lightning">
                  <div className="feature-card__icon">
                    <FlashIcon />
                  </div>
                  <div className="feature-card__content">
                    <h4>{t('Lightning Rounds')}</h4>
                    <p>
                      {isChipPlacement
                        ? t(
                            'Random rounds where one or more options get a boosted multiplier. The boost is hidden until betting closes — when the race starts, the lightning strikes and the boosted options light up.'
                          )
                        : t(
                            'Random rounds with boosted multipliers. Rarity ranges from Common to Legendary.'
                          )}
                    </p>
                  </div>
                </div>
              )}

              {/* Streak Multipliers don't apply on chip-placement games —
                streaks are only tracked / applied for classic single-bet
                play (see register_bet.go's IsChipPlacement gate). Hide the
                feature card so we don't advertise something that won't
                fire. Crash also has no streak system. */}
              {!isChipPlacement && !isCrashGame && (
                <div className="feature-card feature-card--streak">
                  <div className="feature-card__icon">
                    <FireIcon />
                  </div>
                  <div className="feature-card__content">
                    <h4>{t('Streak Multipliers')}</h4>
                    <p>{t('Win streaks give increasing boosts. Losing? Get a comeback boost.')}</p>
                  </div>
                </div>
              )}

              <div className="feature-card feature-card--checkin">
                <div className="feature-card__icon">
                  <GiftIcon />
                </div>
                <div className="feature-card__content">
                  <h4>{t('Daily Check-in')}</h4>
                  <p>
                    {t(
                      'Check in daily for multiplier boosts and rewards that stack with other bonuses.'
                    )}
                  </p>
                </div>
              </div>

              <div className="feature-card feature-card--level">
                <div className="feature-card__icon">
                  <ShieldIcon />
                </div>
                <div className="feature-card__content">
                  <h4>{t('Player Levels')}</h4>
                  <p>{t('Earn XP by wagering to unlock permanent multiplier boosts.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`section section-collapsible ${
          activeSectionId === 'return-to-player' ? 'open' : ''
        }`}
      >
        <div className="section-header" onClick={() => handleSectionClick('return-to-player')}>
          <h3>{t('Return to Player (RTP)')}</h3>
          <div className="section-header-icon">
            <AngleRightIcon />
          </div>
        </div>
        <div className="section-content">
          {isCrashGame ? (
            <>
              <p>{t('Crash returns 94% to players over time (RTP).')}</p>

              <div className="info-box margin-bottom">
                {t(
                  'Every round runs on the same math: the crash point is 0.94 ÷ (1 − r), where r is a provably-fair random value between 0 and 1. That 94% expected return holds at any cash-out target you choose — cashing out early trades a smaller win for a better chance of hitting it; holding longer does the opposite. Your expected return is the same either way.'
                )}
              </div>

              <div className="info-box">
                <strong>{t('Important Notes:')}</strong>{' '}
                {t(
                  'RTP is theoretical, measured over a large number of rounds — not a guarantee for any single session.'
                )}
              </div>
            </>
          ) : (
            <>
              <p>
                {t(
                  'Each betting mode has its own RTP (Return to Player) percentage and house edge. Below are the theoretical returns for each betting type:'
                )}
              </p>

              {game.maxPickableMarbles === 1 ? (
                <SingleRtpTable gameType={game.track.gameType} />
              ) : (
                <FullRtpTable
                  gameType={game.track.gameType}
                  maxPickableMarbles={game.maxPickableMarbles}
                  totalMarbles={game.totalMarbles}
                  equalOdds={equalOdds}
                  isChipPlacement={isChipPlacement}
                />
              )}

              <div className="info-box">
                <strong>{t('Important Notes:')}</strong>{' '}
                {t(
                  'RTP percentages are theoretical and calculated over a large number of plays and not a guarantee for a single session.'
                )}{' '}
                {isChipPlacement
                  ? t(
                      '*Effective RTP includes the average contribution from Lightning Rounds and Player Level boosters over many bets.'
                    )
                  : t(
                      '*Effective RTP includes the average contribution from Lightning Rounds, Streak Multipliers, and Player Level boosters over many bets.'
                    )}
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className={`section section-collapsible ${activeSectionId === 'faq' ? 'open' : ''}`}
        id="faq"
      >
        <div className="section-header" onClick={() => handleSectionClick('faq')}>
          <h3>{t('FAQ')}</h3>
          <div className="section-header-icon">
            <AngleRightIcon />
          </div>
        </div>
        <div className="section-content">
          {/* v43 — crash hides the Free Bets + Multiplier Boosters FAQ
              entries (owner: neither applies to the crash flow yet). */}
          {!isCrashGame && (
            <div className="info-box margin-bottom">
              <h3>{t('What are Free Bets?')}</h3>
              <p>
                {t(
                  'Free bets are bonus funds that can be used to place bets without using your real balance. When you win with a free bet, you receive the winnings minus the original stake amount.'
                )}
              </p>
              <ul>
                {isChipPlacement ? (
                  <>
                    <li>
                      {t(
                        'Free bets auto-apply to the first option you place a chip on. The option locks to the free-bet amount — no extra chips can be added on top.'
                      )}
                    </li>
                    <li>
                      {t(
                        'If you have multiple free bets, the next ones apply in order to the next options you place chips on.'
                      )}
                    </li>
                    <li>{t('Some free bets may only be valid for specific games')}</li>
                  </>
                ) : (
                  <>
                    <li>{t('Only one free bet can be active at a time')}</li>
                    <li>{t('Some free bets may only be valid for specific games')}</li>
                    <li>{t('Check the item details in your inventory for game eligibility')}</li>
                  </>
                )}
              </ul>
            </div>
          )}

          {!isCrashGame && (
            <div className="info-box margin-bottom">
              <h3>{t('What are Multiplier Boosters?')}</h3>
              <p>
                {t(
                  'Multiplier boosters provide a percentage boost on top of your base multiplier. They can be earned through daily check-ins, promotions, or other rewards.'
                )}
              </p>
              <ul>
                {isChipPlacement ? (
                  <>
                    <li>
                      {t(
                        'Boosters auto-apply to the option you place each chip on, oldest booster first. Each booster is consumed when the bet is placed.'
                      )}
                    </li>
                    <li>{t('Boosters stack with Lightning Rounds and Player Level boosts.')}</li>
                  </>
                ) : (
                  <>
                    <li>{t('Some boosters are applied automatically on your next bet')}</li>
                    <li>{t('Manual boosters can be activated from your inventory')}</li>
                    <li>{t('Boosters stack with Lightning Rounds and streak bonuses')}</li>
                  </>
                )}
              </ul>
            </div>
          )}

          <div className="info-box margin-bottom">
            <h3>{t('Is there a maximum payout per bet?')}</h3>
            <p>
              {t(
                'Yes — each operator sets a maximum payout per bet. When the combined effect of your base multiplier and any active boosts (lightning rounds, multiplier boosters, streak bonuses, level boosts) would push your potential payout above this limit, the payout is capped at the operator’s maximum.'
              )}
            </p>
            <ul>
              <li>
                {t('Your bet is still placed normally — the cap only limits the maximum win')}
              </li>
              <li>
                {t(
                  'The capped amount is shown as the potential payout in your bet history, so what you see is what you can win'
                )}
              </li>
              <li>
                {t(
                  'The cap is set per operator and applies to all bets — including high-multiplier outcomes during lightning rounds'
                )}
              </li>
            </ul>
          </div>

          {isCrashGame ? (
            <>
              <div className="info-box margin-bottom">
                <h3>{t('What if I don’t cash out in time?')}</h3>
                <p>
                  {t(
                    'If the round crashes before you cash out, that stake is lost — there’s no partial return. The whole game is collecting before the walk-in. Set an auto cash-out target if you’d rather not rely on reflexes.'
                  )}
                </p>
              </div>

              <div className="info-box margin-bottom">
                <h3>{t('What decides the crash point?')}</h3>
                <p>
                  {t(
                    'A provably-fair random value fixed before the round starts — not the footage. The clip is chosen to match the pre-decided crash point, so the walk-in always lands exactly where the math says. Open any past round to recompute and verify it.'
                  )}
                </p>
              </div>
            </>
          ) : isChipPlacement ? (
            <div className="info-box margin-bottom">
              <h3>{t('What happens to my items?')}</h3>
              <p>
                {t(
                  'Free bets and multiplier boosters apply automatically when you place chips. The first option you place a chip on uses your oldest free bet (locking that option to the free-bet amount); subsequent options consume the next available booster, and so on.'
                )}
              </p>
            </div>
          ) : (
            <div className="info-box margin-bottom">
              <h3>{t('Where do I find my items?')}</h3>
              <p>
                {t(
                  'Click the inventory icon to view and manage your items. This includes free bets, multiplier boosters, and any collectibles you have earned.'
                )}
              </p>
            </div>
          )}

          {(isIrlAltGame || isIrlGame) && (
            <div className="info-box margin-bottom">
              <h3>
                {isSnowRunGame
                  ? t('Why does the count differ from what I see?')
                  : isDuckRiverGame
                    ? t("Why didn't the AI count all the ducks?")
                    : isSocialBettingGame
                      ? t("Why didn't the AI count everything?")
                      : t("Why didn't the AI count all the vehicles?")}
              </h3>
              <p>
                {isSnowRunGame
                  ? t(
                      "You are predicting the AI's count, not a head count. In POV mode, the dwell-time system re-counts skiers who stay in the zone — you can see the countdown arc and streak badge ticking up. In drone mode, the AI may miss a skier or briefly re-count one due to altitude, overlapping paths, or snow conditions — that's part of what the drone sees. Every detection is shown on screen."
                    )
                  : isDuckRiverGame
                    ? t(
                        "The game uses an AI detection model to count ducks. You are not predicting how many ducks float past — you are predicting how many the AI model will detect. The model's count is the official result, similar to a referee's call in sport."
                      )
                    : isSocialBettingGame
                      ? t(
                          "The game uses an AI detection model to count what's in each clip. You are not predicting how many things were really there — you are predicting how many the AI model will detect. The model's count is the official result, similar to a referee's call in sport."
                        )
                      : t(
                          "The game uses an AI detection model to count vehicles. You are not predicting how many vehicles drive past — you are predicting how many the AI model will detect. The model's count is the official result, similar to a referee's call in sport."
                        )}
              </p>
              <p>
                <strong>{t('Why the AI may not detect every one')}</strong>
              </p>
              <ul>
                <li>
                  {t(
                    'Partial visibility — objects at the edge of frame or partially hidden may not be picked up'
                  )}
                </li>
                <li>
                  {t(
                    'Overlapping objects — when two pass at the same time, the model may register them as one'
                  )}
                </li>
                <li>
                  {t(
                    'Environmental factors — lighting, weather, and camera angle can all affect detection'
                  )}
                </li>
              </ul>
              <p>
                <strong>{t('This is fair because:')}</strong>
              </p>
              <ul>
                <li>{t('Every player sees the same footage and the same detections')}</li>
                <li>{t('The rules and disclaimer are shown before every round')}</li>
                <li>
                  {t(
                    'It works both ways — missed detections can also cause "Under" bets to win that otherwise would have lost'
                  )}
                </li>
                <li>{t('The system is fully automated with zero human intervention')}</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <ErrorHandlingSection
        flags={{
          isCoinFlipGame,
          isCrashGame,
          isDuckRiverGame,
          isFishtankGame,
          isIrlAltGame,
          isIrlGame,
          isSnowRunGame,
          isSocialBettingGame
        }}
        onToggle={() => handleSectionClick('error-handling')}
        open={activeSectionId === 'error-handling'}
      />
    </div>
  )
}
